import { ReviewContractDocumentModal } from '@dealstack/frontend/shared/components/ReviewContractDocumentModal/ReviewContractDocumentModal';
import { PrimarySideListV2 } from '@dealstack/frontend/shared/components/list/PrimarySideList/PrimarySideListV2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useDocumentsController } from '@dealstack/frontend/investor/pages/Documents/useDocumentsController';
import {
	Content,
	InvestmentHeader,
	Layout,
	MainLayout,
} from '@dealstack/frontend/investor/pages/Investment/common/investmentLayout';
import { Flex, Text, Title } from '@mantine/core';
import { useContext } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { DocumentWorkflowContract } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/DocumentWorkflowContract';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';

interface Props {
	className?: string;
}

const Documents = ({ className }: Props) => {
	const tInvestor = useTranslationInvestor();
	const controller = useDocumentsController();
	const { investorId } = useRequiredParams('investorId');

	const { setIsContinueDisabled, dealId, mipId } = useContext(investmentJoinAndAllocationContext);

	const {
		redirectLink,
		selectedDocument,
		setSelectedDocument,
		activeMainStep,
		documentsPageSideBarConfig,
	} = controller;

	return (
		<Layout className={className}>
			<HeaderBar>
				<Link to={redirectLink}>
					<Flex gap="0.5rem" align="center">
						<Icon
							variant="arrow-left"
							size="xl"
							color={defaultTheme.v3.colors.gray[9]}
						/>
						<Text size="lg" c="gray.9">
							{tInvestor('investment.header.back_programme')}
						</Text>
					</Flex>
				</Link>
			</HeaderBar>

			<MainLayout>
				<PrimarySideListV2
					sideListItems={documentsPageSideBarConfig ?? []}
					activeKey={activeMainStep?.key}
				/>
				<Content>
					<InvestmentHeader>
						<Title order={5}>{tInvestor('documents.documents')}</Title>
					</InvestmentHeader>

					<DocumentWorkflowContract
						setIsContinueDisabled={setIsContinueDisabled}
						dealId={dealId}
						mipId={mipId}
						investorId={investorId}
						excludeTsaContracts={true}
					/>
				</Content>
			</MainLayout>

			{selectedDocument && (
				<ReviewContractDocumentModal
					onClose={() => setSelectedDocument(undefined)}
					dealId={selectedDocument?.dealId ?? ''}
					fileId={selectedDocument?.file?.id ?? ''}
					fileName={selectedDocument?.file?.name ?? ''}
				/>
			)}
		</Layout>
	);
};

const HeaderBar = styled.div`
	display: grid;
	align-items: center;
	grid-template-columns: 14rem 1fr min-content;
	grid-gap: 5rem;
	vertical-align: center;
	padding: 1em;
	background-color: ${({ theme }) => theme.v3.colors.dark[8]};
	border-radius: 0.5rem;
	box-shadow: 0 4px 8px 0 #dcdfe0;
`;

const Styled = styled(Documents)`
	display: flex;
	justify-content: center;

	table {
		border-top: none;

		.with-pointer {
			cursor: unset;
		}

		tr,
		td {
			vertical-align: baseline;
		}
	}
`;

export { Styled as Documents };
